import { useCallback, useState } from 'react'
import {
    useFindOneStockAlmacenEquiposMutation,
    useFindStockAlmacenEquiposMutation
} from '../../SliceLogistica'
import { StockAlmacenENTITY, StockAlmacenEquiposENTITY } from 'sigo-entities'
import { useSelector } from 'react-redux'
import { RootState } from '../../AdapterStore'
import { indexedDB } from '../../useIndexedDB'

interface Params {
    StockAlmacen: StockAlmacenENTITY
}

export const useStockAlmacenEquiposRepository = () => {
    const { user } = useSelector((state: RootState) => state.auth)

    const [_findOneStockAlmacenEquipos, { isError, error }] = useFindOneStockAlmacenEquiposMutation()
    const [_findStockAlmacenEquipos, { isError: isErrorFindStock, error: errorFindStock }] = useFindStockAlmacenEquiposMutation()

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<StockAlmacenEquiposENTITY[]>([])
    const [dataEquiposAlmacen, setDataEquiposAlmacen] = useState<StockAlmacenEquiposENTITY>(new StockAlmacenEquiposENTITY())

    const findStockAlmacenEquipos = useCallback(async ({ pipeline }: { pipeline: any[] }) => {
        setIsLoading(true)
        try {
            // ONLINE
            if (navigator.onLine) {
                const response = await _findStockAlmacenEquipos({
                    pais: user.Pais.Codigo,
                    delegacion: user.Delegacion.Codigo,
                    pipeline
                }).unwrap()
                const now = Date.now()

                await indexedDB.stock_almacen_equipos.clear()
                await indexedDB.stock_almacen_equipos.bulkPut(
                    response.map(e => ({
                        ...e,
                        pendingSync: 0,
                        syncedAt: now,
                        updatedAt: now
                    }))
                )

                setData(response)
                return response
            }

            // OFFLINE
            const localData = await indexedDB.stock_almacen_equipos.toArray()
            setData(localData)
            return localData
        } finally {
            setIsLoading(false)
        }
    }, [_findStockAlmacenEquipos])

    const findOneStockAlmacenEquipos = useCallback(async ({ StockAlmacen }: Params) => {
        setIsLoading(true)
        try {
            // ONLINE
            if (navigator.onLine) {
                const pipeline = [
                    {
                        $match: {
                            CodigoLlave: StockAlmacen.CodigoLlave,
                            CodigoBusqueda: StockAlmacen.CodigoBusqueda,
                            ID_StockAlmacen: StockAlmacen.ID_StockAlmacen,
                        }
                    },
                    {
                        $addFields: {
                            Equipos: {
                                $filter: {
                                    input: '$Equipos',
                                    as: 'equipo',
                                    cond: { $eq: ['$$equipo.Estado.ID_Estado', 2] }
                                }
                            }
                        }
                    }
                ]
                const response = await _findOneStockAlmacenEquipos({
                    pais: user.Pais.Codigo,
                    delegacion: user.Delegacion.Codigo,
                    pipeline
                }).unwrap()
                // Se cachea para que quede disponible offline tras la primera
                // consulta exitosa del día (antes solo se cacheaba vía el
                // prefetch masivo de la pantalla de Logística, que el flujo de
                // Somacyl nunca dispara).
                const now = Date.now()
                await indexedDB.stock_almacen_equipos.put({
                    ...response,
                    pendingSync: 0,
                    syncedAt: now,
                    updatedAt: now
                })
                setDataEquiposAlmacen(response)
                return response
            }
            // OFFLINE
            const localData = await indexedDB.stock_almacen_equipos.toArray()
            const response = localData.filter(item =>
                item.ID_StockAlmacen === StockAlmacen.ID_StockAlmacen
                && item.CodigoBusqueda === StockAlmacen.CodigoBusqueda
                && item.CodigoLlave === StockAlmacen.CodigoLlave)

            if (response?.length !== 1) {
                throw new Error('No se pudo obtener los equipos para el stock seleccionado')
            }
            response[0].Equipos = response[0].Equipos.filter((e) => e.Estado.ID_Estado === 2)
            setDataEquiposAlmacen(response[0])
            return response[0]
        } finally {
            setIsLoading(false)
        }
    }, [user, _findOneStockAlmacenEquipos])

    return {
        data,
        isLoading,
        findOneStockAlmacenEquipos,
        isError: isError || isErrorFindStock,
        error: error || errorFindStock,
        dataEquiposAlmacen,
        findStockAlmacenEquipos
    }
}